import { useState, useCallback, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useUserPOIs } from './useUserPOIs'
import { UserPOI, CreateUserPOIData, UserPOIType } from '../types/userPois'
import { UserPOIsPanel } from '../components/userPois/UserPOIsPanel'
import { AddUserPOIModal } from '../components/userPois/AddUserPOIModal'
import { UserPOIDetailModal } from '../components/userPois/UserPOIDetailModal'
import type { RouteData } from '@cya-routes/shared'

interface UseUserPOISystemProps {
  routeId: string
  route?: RouteData
  userId?: string
  userDisplayName?: string
  userPhotoURL?: string
  mapManager: React.MutableRefObject<any>
  userPoiRenderer: React.MutableRefObject<any>
  renderersInitialized: boolean
}

export function useUserPOISystem({
  routeId,
  route,
  userId,
  userDisplayName,
  userPhotoURL,
  mapManager,
  userPoiRenderer,
  renderersInitialized
}: UseUserPOISystemProps) {
  const [showUserPOIs, setShowUserPOIs] = useState(true)
  const [showResolved, setShowResolved] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [addModalCoords, setAddModalCoords] = useState<[number, number] | undefined>()
  const [addModalType, setAddModalType] = useState<UserPOIType | undefined>()
  const [selectedPOI, setSelectedPOI] = useState<UserPOI | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const {
    userPOIs,
    loading,
    error,
    addUserPOI,
    editUserPOI,
    removeUserPOI,
    confirmPOI,
    markOutdated,
    removeVote,
    refreshPOIs
  } = useUserPOIs({
    routeId,
    userId,
    userDisplayName,
    userPhotoURL
  })

  // Define all callbacks before useEffects that use them
  const handlePOIUpdate = useCallback(async (poiId: string, coordinates: { lng: number; lat: number }) => {
    try {
      await editUserPOI(poiId, { coordinates })
      await refreshPOIs()
    } catch (error) {
      console.error('Failed to update POI location:', error)
    }
  }, [editUserPOI, refreshPOIs])

  const handlePOIClick = useCallback((poi: UserPOI) => {
    setSelectedPOI(poi)
    setShowDetailModal(true)
    
    // Fly to POI on the map
    if (userPoiRenderer.current) {
      userPoiRenderer.current.flyToPOI(poi)
    }
  }, [userPoiRenderer])

  const handleStartMove = useCallback((poiId: string) => {
    if (userPoiRenderer.current) {
      userPoiRenderer.current.startMovePOI(poiId)
    }
  }, [userPoiRenderer])

  const handleAddPOI = useCallback(async (data: CreateUserPOIData) => {
    try {
      await addUserPOI(data)
      setShowAddModal(false)
      setAddModalCoords(undefined)
      setAddModalType(undefined)
    } catch (error) {
      console.error('Failed to add user POI:', error)
    }
  }, [addUserPOI])

  const handleEditPOI = useCallback(async (poiId: string, name: string, description: string, photos?: string[]) => {
    try {
      await editUserPOI(poiId, { name, description, photos })
      setShowDetailModal(false)
      await refreshPOIs()
    } catch (error) {
      console.error('Failed to edit POI:', error)
    }
  }, [editUserPOI, refreshPOIs])

  const handleDeletePOI = useCallback(async (poiId: string) => {
    try {
      await removeUserPOI(poiId)
      setShowDetailModal(false)
    } catch (error) {
      console.error('Failed to delete POI:', error)
    }
  }, [removeUserPOI])

  const handleResolvePOI = useCallback(async (poiId: string, resolved: boolean) => {
    try {
      await editUserPOI(poiId, { resolved })
      if (resolved && !showResolved) {
        setShowDetailModal(false)
      }
      await refreshPOIs()
    } catch (error) {
      console.error('Failed to update POI:', error)
    }
  }, [editUserPOI, refreshPOIs, showResolved])

  const handleStartDrag = useCallback((type: UserPOIType) => {
    setIsDragging(true)
    
    // Add visual feedback to the map
    if (mapManager.current) {
      const map = mapManager.current.getMap()
      if (map) {
        map.getCanvas().style.cursor = 'copy'
      }
    }
  }, [mapManager])

  // Update POI rendering when POIs or visibility changes
  useEffect(() => {
    if (userPoiRenderer?.current && renderersInitialized) {
      if (showUserPOIs) {
        // Filter POIs based on resolved status
        const visiblePOIs = showResolved ? userPOIs : userPOIs.filter(poi => !poi.resolved)
        console.log('📍 Rendering user POIs:', visiblePOIs.length)
        userPoiRenderer.current.renderUserPOIs(visiblePOIs, selectedPOI?.id, userId)
      } else {
        userPoiRenderer.current.clearMarkers()
      }
    }
  }, [userPOIs, showUserPOIs, showResolved, selectedPOI?.id, userPoiRenderer, renderersInitialized, userId])


  // Set up POI callbacks when renderer is ready
  useEffect(() => {
    if (userPoiRenderer?.current && renderersInitialized) {
      // Set up click handler
      userPoiRenderer.current.setOnPOIClick(handlePOIClick)
      
      // Set up update handler
      if (handlePOIUpdate) {
        userPoiRenderer.current.setOnPOIUpdate(handlePOIUpdate)
      }
    }
  }, [userPoiRenderer, renderersInitialized, handlePOIClick, handlePOIUpdate])

  // Set up map as drop zone for user POIs
  useEffect(() => {
    if (!mapManager.current || !renderersInitialized) return

    const map = mapManager.current.getMap()
    if (!map) return

    const mapContainer = map.getContainer()

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
      e.dataTransfer!.dropEffect = 'copy'
    }

    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      
      const poiType = e.dataTransfer?.getData('userPoiType') as UserPOIType
      if (!poiType || !userId) return

      // Get coordinates from mouse position
      const rect = mapContainer.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      // Convert to map coordinates
      const lngLat = map.unproject([x, y])
      
      setAddModalCoords([lngLat.lng, lngLat.lat])
      setAddModalType(poiType)
      setShowAddModal(true)
      setIsDragging(false)
      
      // Reset cursor
      map.getCanvas().style.cursor = ''
    }

    const handleDragLeave = (e: DragEvent) => {
      // Only set dragging to false if we're leaving the map container
      if (e.target === mapContainer) {
        setIsDragging(false)
        map.getCanvas().style.cursor = ''
      }
    }

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer?.types.includes('userpoitype')) {
        map.getCanvas().style.cursor = 'copy'
      }
    }

    mapContainer.addEventListener('dragover', handleDragOver)
    mapContainer.addEventListener('drop', handleDrop)
    mapContainer.addEventListener('dragleave', handleDragLeave)
    mapContainer.addEventListener('dragenter', handleDragEnter)

    return () => {
      mapContainer.removeEventListener('dragover', handleDragOver)
      mapContainer.removeEventListener('drop', handleDrop)
      mapContainer.removeEventListener('dragleave', handleDragLeave)
      mapContainer.removeEventListener('dragenter', handleDragEnter)
    }
  }, [mapManager, renderersInitialized, userId])

  return {
    // Data and state
    userPOIs,
    showUserPOIs,
    setShowUserPOIs,
    showResolved,
    setShowResolved,
    loading,
    // Handlers
    handlePOIClick,
    handleStartDrag,
    // Sidebar item to be included in parent component (only Add functionality)
    sidebarItem: {
      id: 'user-pois-add',
      icon: <AlertTriangle className="w-5 h-5" />,
      label: 'Add Report',
      content: (
        <UserPOIsPanel
          pois={userPOIs}
          route={route}
          onPOIClick={handlePOIClick}
          onStartDrag={handleStartDrag}
          showResolved={showResolved}
          onToggleResolved={() => setShowResolved(!showResolved)}
          isAuthenticated={!!userId}
          mobileMode={true} // Force to only show Add tab (no View tab)
        />
      )
    },
    // Modals to be rendered
    modals: (
      <>
        {/* Add User POI Modal */}
        {addModalCoords && (
          <AddUserPOIModal
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false)
              setAddModalCoords(undefined)
              setAddModalType(undefined)
            }}
            onSubmit={handleAddPOI}
            routeId={routeId}
            coordinates={addModalCoords}
            initialType={addModalType}
            userId={userId || ''}
          />
        )}

        {/* User POI Detail Modal */}
        <UserPOIDetailModal
          poi={selectedPOI ? userPOIs.find(p => p.id === selectedPOI.id) || selectedPOI : null}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedPOI(null)
          }}
          onConfirm={userId ? confirmPOI : undefined}
          onMarkOutdated={userId ? markOutdated : undefined}
          onRemoveVote={userId ? removeVote : undefined}
          onEdit={userId === selectedPOI?.authorId ? handleEditPOI : undefined}
          onDelete={userId === selectedPOI?.authorId ? handleDeletePOI : undefined}
          onResolve={userId === selectedPOI?.authorId ? handleResolvePOI : undefined}
          onStartMove={userId === selectedPOI?.authorId ? handleStartMove : undefined}
          currentUserId={userId}
          currentUserName={userDisplayName}
          currentUserPhoto={userPhotoURL}
        />
      </>
    ),
    // Expose POI update handler for map lifecycle
    handlePOIUpdate
  }
}